import { jsx as _jsx } from "react/jsx-runtime";
/**
 * Phase 336 wave 1 task 1.2 (#1163) — the A/R/B/C priority chip that sits on every
 * Vacancies role row.
 *
 * A/R/B/C is ORDINAL, not categorical: it is a confidence ramp, so it takes WEIGHT
 * rather than one of the nine categorical hues (`charts/consultantPalette.ts` owns
 * those, and a consultant's identity colour must never be confused with a role's
 * priority). The four visual variants are the pinned design's (phase 336 ask-pin
 * decision 1):
 *
 *   A — SOLID fill.    A and R both count toward the A+R headline, and A is the
 *                      strongest claim, so it gets the heaviest treatment.
 *   R — OUTLINE only.  Transparent fill, indigo rule + indigo letter: same hue
 *                      family as A (they are counted together), visibly lighter
 *                      weight (it is the "R" half of the headline).
 *   B — MUTED.         Visible everywhere, EXCLUDED from the headline.
 *   C — MUTED.         Same muted treatment as B, its own token pair.
 *
 * Colour comes only from the `--ard-prio-*` token pairs in
 * `packages/design-tokens/colors_and_type.css`, whose letter-on-fill contrast is
 * re-measured (>= 4.5:1, AA — the letter is text) by
 * `test/fence/dataviz-tokens.fence.test.ts`. No hex is spelled in this file.
 *
 * <b>Deliberate reading of the plan's R spec.</b> PLAN 336 task 1.2 writes R's edge
 * as `--ard-prio-a-bg`. That token is the SOLID FILL tint (1.60:1 on bone): used as
 * a 1px rule it all but vanishes on paper, which is why the shipped token block
 * (task 1.1, `colors_and_type.css` § Priority chips) records the rule "for the
 * OUTLINE treatment (R) the fg token is the border as well, so the rule matches the
 * letter instead of vanishing into the paper". This component follows the token
 * file: R's edge is `--ard-prio-r` — the same federal-indigo family the plan asks
 * for (A and R share the hue), at a weight that actually renders.
 *
 * <b>Fail-loud on an unknown letter.</b> A/R/B/C is a CLOSED vocabulary owned by the
 * forecast payload. An unrecognised letter means the wire mirror has drifted, and a
 * silently-rendered fallback chip would misreport a role's priority to the client —
 * so the chip throws, in every environment. It is the parser posture of the #1161
 * priority ingestion, carried to the render side: better a loud error boundary than
 * a quietly wrong headline.
 */
/** The closed priority vocabulary, in headline order (A and R count; B and C do not). */
export const PRIORITIES = ["A", "R", "B", "C"];
/**
 * The letter → (variant, token pair) map. This is the ONE place a priority's visual
 * weight is decided; the pages consume the chip, never these tokens directly.
 */
const PRIORITY_SPEC = {
    A: {
        variant: "solid",
        fill: "var(--ard-prio-a-bg)",
        ink: "var(--ard-prio-a)",
        edge: "var(--ard-prio-a-bg)",
        label: "A",
    },
    R: {
        variant: "outline",
        fill: "transparent",
        ink: "var(--ard-prio-r)",
        edge: "var(--ard-prio-r)",
        label: "R",
    },
    B: {
        variant: "muted",
        fill: "var(--ard-prio-b-bg)",
        ink: "var(--ard-prio-b)",
        edge: "var(--ard-prio-b-bg)",
        label: "B",
    },
    C: {
        variant: "muted",
        fill: "var(--ard-prio-c-bg)",
        ink: "var(--ard-prio-c)",
        edge: "var(--ard-prio-c-bg)",
        label: "C",
    },
};
/**
 * Narrows an arbitrary wire value to a {@link Priority}, throwing on anything else.
 * Exported so a selector layer can fail at the projection boundary (with the row in
 * hand) rather than deep inside a render.
 *
 * Case is significant: a lowercase letter means the mirror is transcribing the wire
 * loosely, and normalising it here would hide that from the tests that exist to
 * catch it.
 */
export function parsePriority(value) {
    if (typeof value === "string" && PRIORITIES.includes(value)) {
        return value;
    }
    throw new Error(`PriorityChip: unknown priority ${JSON.stringify(value)} — the vocabulary is closed ` +
        `(${PRIORITIES.join(" | ")}). An unrecognised letter means the forecast payload mirror ` +
        `has drifted; rendering a fallback chip would misreport this role's priority.`);
}
export function PriorityChip({ priority, className }) {
    const letter = parsePriority(priority);
    const spec = PRIORITY_SPEC[letter];
    const cls = [
        "prio-chip",
        `prio-chip--${letter.toLowerCase()}`,
        `prio-chip--${spec.variant}`,
        className ?? "",
    ]
        .filter(Boolean)
        .join(" ");
    return (_jsx("span", { className: cls, role: "img", "aria-label": `Priority ${spec.label}`, style: {
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            minWidth: "1.5em",
            padding: "1px 6px",
            borderRadius: "var(--radius-sm)",
            border: `1px solid ${spec.edge}`,
            background: spec.fill,
            color: spec.ink,
            font: "600 11px/1.5 var(--font-mono)",
            letterSpacing: "0.04em",
        }, children: letter }));
}
export default PriorityChip;
//# sourceMappingURL=PriorityChip.js.map